import { Box, Grid, Paper, TextField, MenuItem, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import axiosClient from "../../api/axiosClient";
import Layout from "../../components/Layout";
import { PumpPortRequest } from "./PumpPortRequest";
import { PumpPortResponse } from "./PumpPortResponse";
import { searchGridSx } from "../../utils/responsiveStyles";

export default function PumpPort() {
  // 🔹 Table state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);
  const [sortModel, setSortModel] = useState([]);

  // 🔹 Map frontend field names → backend DB names
  const orderByMap = {
    id: "id",
    protocol: "protocol",
    baudRate: "baud_rate",
  };

  // 🔹 Dropdown options (from API)
  const [protocols, setProtoCols] = useState([]);
  const [baudRates, setBaudRates] = useState([]);

  // 🔹 Search filters
  const [protocol, setProtoCol] = useState(null);
  const [baudRate, setBaudRate] = useState(null);

  useEffect(() => {
    axiosClient
      .get("/pump_port/get-pump-protocol-lookup") // ✅ your backend endpoint for dropdowns
      .then((res) => {
        const data = res.data;
        console.log("device types: " + JSON.stringify(data));
        setProtoCols(data || []);
      })
      .catch((err) => console.error("Dropdown fetch failed", err));

    axiosClient
      .get("/pump_port/get-pump-baud-rate-lookup") // ✅ your backend endpoint for dropdowns
      .then((res) => {
        const data = res.data;
        console.log("device state: " + JSON.stringify(data));
        setBaudRates(data || []);
      })
      .catch((err) => console.error("Dropdown fetch failed", err));
  }, []);

  // 🔹 Fetch Data
  const fetchData = () => {
    const token = localStorage.getItem("token");
    console.log("token: " + token);
    setLoading(true);

    const request = new PumpPortRequest();
    request.setStartIndex(page * pageSize);
    request.setLimit(pageSize);

    if (sortModel.length > 0) {
      const sort = sortModel[0]; // { field: "codeDescription", sort: "asc" }
      var backendField = orderByMap[sort.field] || sort.field;
      console.log(`order by ${backendField} ${sort.sort?.toUpperCase()}`);
      backendField =
        backendField === "protocol"
          ? "pr.name"
          : backendField === "baud_rate"
          ? "b." + backendField
          : "p." + backendField;
      request.setOrderBy(
        `order by ${backendField} ${sort.sort?.toUpperCase()}`
      );
    }

    if (protocol) request.setProtocol(protocol);
    if (baudRate) request.setBaudRate(baudRate);

    axiosClient
      .post("/pump_port/get-pump-port", request)
      .then((res) => {
        const pumpResponse = new PumpPortResponse(res.data);
        if (
          pumpResponse?.getOperationResult() &&
          pumpResponse.getPumpPortList()
        ) {
          setRows(pumpResponse.getPumpPortList());
          setRowCount(pumpResponse.getCount());
        } else {
          setRows([]);
          setRowCount(0);
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  // initial load + refetch when page/pageSize changes
  useEffect(() => {
    console.log(page, pageSize);
    fetchData();
  }, [page, pageSize, sortModel]);

  // 🔹 Columns
  const columns = [
    { field: "id", headerName: "Port", width: 90 },
    { field: "protocol", headerName: "Protocol", flex: 1 },
    { field: "baudRate", headerName: "Baud Rate", flex: 1 },
  ];

  return (
    <Layout title="Pump Port">
      {/* Search Form */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
        <Grid container sx={searchGridSx}>
          {/* search fields ... */}
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              label="Protocol"
              value={protocol}
              onChange={(e) => setProtoCol(e.target.value)}
              fullWidth
              sx={{ minWidth: { sm: 180 } }}
            >
              <MenuItem value="">All</MenuItem>
              {protocols.map((dt, i) => (
                <MenuItem key={i} value={dt.id}>
                  {dt.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              label="Baud Rate"
              value={baudRate}
              onChange={(e) => setBaudRate(e.target.value)}
              fullWidth
              sx={{ minWidth: { sm: 180 } }}
            >
              <MenuItem value="">All</MenuItem>
              {baudRates.map((dt, i) => (
                <MenuItem key={i} value={dt.id}>
                  {dt.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              sx={{ minWidth: { sm: 180 } }}
              onClick={fetchData}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* DataGrid */}
      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pagination
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(model) => setSortModel(model)}
          sortingOrder={["asc", "desc"]}
          loading={loading}
          pageSizeOptions={[5, 10, 20, 50]}
          autoHeight
          sx={{ minWidth: { xs: 640, sm: "100%" }, bgcolor: "white" }}
        />
      </Box>
    </Layout>
  );
}
